<template>
  <div>
    <!-- Data Table -->
    <div
      v-if="rows && rows.length > 0"
      data-testid="transaction-matching-consolidated-results__table-wrapper"
      class="consolidated-results__table-wrapper"
    >
      <div class="consolidated-results__table-scroll">
        <table
          data-testid="transaction-matching-consolidated-results__table"
          class="consolidated-results__table"
        >
          <thead class="consolidated-results__thead">
            <tr class="consolidated-results__header-row">
              <th
                v-for="(header, index) in tableHeaders"
                :key="header.key"
                data-testid="transaction-matching-consolidated-results__header-cell"
                :class="[
                  'consolidated-results__header-cell',
                  { 'consolidated-results__header-cell--draggable': draggableHeaders }
                ]"
                :draggable="draggableHeaders"
                @dragstart="onDragStart(header.key, index, $event)"
                @dragover.prevent="onDragOver(header.key, index, $event)"
                @dragleave="onDragLeave($event)"
                @drop="onDrop(header.key, index, $event)"
                @dragend="onDragEnd"
              >
                <div class="consolidated-results__header-content">
                  <!-- Drag Icon -->
                  <img
                    v-if="draggableHeaders"
                    src="/img/icons/fbc-icons/drag-drop.svg"
                    alt="Drag"
                    class="consolidated-results__drag-icon"
                    data-testid="transaction-matching-consolidated-results__drag-icon"
                  />

                  <span
                    :class="[
                      'consolidated-results__header-label',
                      getSortEntry(header.key) ? 'consolidated-results__header-label--sorted' : ''
                    ]"
                  >
                    <span>{{ header.label }}</span>

                    <!-- Sort Indicators (display only) -->
                    <div v-if="getSortEntry(header.key)" class="consolidated-results__sort-indicators">
                      <svg
                        v-if="getSortEntry(header.key).direction === 'asc'"
                        class="consolidated-results__sort-icon"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path d="M5 15l7-7 7 7" />
                      </svg>
                      <svg
                        v-else
                        class="consolidated-results__sort-icon"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19 9l-7 7-7-7" />
                      </svg>
                      <span
                        v-if="appliedSort.length > 1"
                        class="consolidated-results__sort-order"
                      >
                        {{ getSortIndex(header.key) + 1 }}
                      </span>
                    </div>
                  </span>

                  <!-- Filter Indicator -->
                  <span
                    v-if="isColumnFiltered(header.key)"
                    class="consolidated-results__filter-indicator"
                    :title="'Filtered by ' + getFilterCount(header.key) + ' condition(s)'"
                  >
                    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path d="M22 3L2 3 10 12.46 10 19 14 21 14 12.46 22 3z" />
                    </svg>
                  </span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="consolidated-results__tbody">
            <tr
              v-for="(row, rowIndex) in rows"
              :key="row._id || rowIndex"
              data-testid="transaction-matching-consolidated-results__row"
              class="consolidated-results__row"
            >
              <td
                v-for="header in tableHeaders"
                :key="header.key"
                data-testid="transaction-matching-consolidated-results__cell"
                :class="getCellClass(header)"
              >
                <!-- Status with Square Indicator -->
                <template v-if="isStatusColumn(header.key)">
                  <div class="consolidated-results__status-container">
                    <span :class="getStatusIndicatorClass(row[header.key])"></span>
                    <v-tooltip
                      :bottom="rowIndex === 0"
                      :top="rowIndex !== 0"
                      :content-class="rowIndex === 0 ? 'tooltip-bottom' : 'tooltip-top'"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          class="consolidated-results__status-text"
                          v-bind="attrs"
                          @mouseenter="(e) => { if (isCellTruncated(e.currentTarget)) on.mouseenter(e) }"
                          @mouseleave="on.mouseleave"
                        >{{ row[header.key] }}</span>
                      </template>
                      <span>{{ row[header.key] }}</span>
                    </v-tooltip>
                  </div>
                </template>

                <!-- Numeric Values -->
                <template v-else-if="header.isNumeric">
                  <v-tooltip
                    :bottom="rowIndex === 0"
                    :top="rowIndex !== 0"
                    :content-class="rowIndex === 0 ? 'tooltip-bottom' : 'tooltip-top'"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        :class="getNumberClass(row[header.key])"
                        v-bind="attrs"
                        @mouseenter="(e) => { if (isCellTruncated(e.currentTarget)) on.mouseenter(e) }"
                        @mouseleave="on.mouseleave"
                      >
                        {{ formatNumber(row[header.key]) }}
                      </span>
                    </template>
                    <span>{{ formatNumber(row[header.key]) }}</span>
                  </v-tooltip>
                </template>

                <!-- Default Text -->
                <template v-else>
                  <v-tooltip
                    :bottom="rowIndex === 0"
                    :top="rowIndex !== 0"
                    :content-class="rowIndex === 0 ? 'tooltip-bottom' : 'tooltip-top'"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        class="consolidated-results__cell-text"
                        v-bind="attrs"
                        @mouseenter="(e) => { if (isCellTruncated(e.currentTarget)) on.mouseenter(e) }"
                        @mouseleave="on.mouseleave"
                      >{{ row[header.key] }}</span>
                    </template>
                    <span>{{ row[header.key] }}</span>
                  </v-tooltip>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import {
  formatNumber,
  getCellClass,
  getStatusIndicatorClass,
  getNumberClass
} from './tmResultsDataTableUtils.js';

export default {
  name: 'TMResultsTableWrapper',

  props: {
    tableHeaders: {
      type: Array,
      default: () => []
    },
    rows: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    draggableHeaders: {
      type: Boolean,
      default: true
    },
    variant: {
      type: String,
      default: 'v1',
      validator: (value) => ['v1', 'v2'].includes(value)
    },
    appliedSort: {
      type: Array,
      default: () => []
    },
    appliedFilters: {
      type: Object,
      default: () => ({ groups: [], group_connectors: [] })
    },
    columnTypeMap: {
      type: Object,
      default: () => ({})
    },
    statusColumnKeys: {
      type: Array,
      default: () => ['Consol Status']
    }
  },

  methods: {
    isStatusColumn(key) {
      return this.statusColumnKeys.includes(key);
    },

    isCellTruncated(el) {
      if (!el) return false;
      if (el.scrollWidth > el.clientWidth) return true;
      const td = el.closest ? el.closest('td') : null;
      return td ? td.scrollWidth > td.clientWidth : false;
    },

    getSortEntry(key) {
      return this.appliedSort.find((item) => item.key === key);
    },

    getSortIndex(key) {
      return this.appliedSort.findIndex((item) => item.key === key);
    },

    isColumnFiltered(columnKey) {
      if (!this.appliedFilters.groups || this.appliedFilters.groups.length === 0) {
        return false;
      }

      return this.appliedFilters.groups.some(
        (group) =>
          group.conditions && group.conditions.some((condition) => condition.column === columnKey)
      );
    },

    getFilterCount(columnKey) {
      if (!this.appliedFilters.groups || this.appliedFilters.groups.length === 0) {
        return 0;
      }

      let count = 0;
      this.appliedFilters.groups.forEach((group) => {
        if (group.conditions) {
          count += group.conditions.filter((condition) => condition.column === columnKey).length;
        }
      });

      return count;
    },

    getCellClass(header) {
      return getCellClass(header, this.columnTypeMap);
    },

    getStatusIndicatorClass(status) {
      return getStatusIndicatorClass(status);
    },

    getNumberClass(value) {
      return getNumberClass(value);
    },

    formatNumber(num) {
      return formatNumber(num);
    },

    onDragStart(headerKey, index, event) {
      if (!this.draggableHeaders) return;
      this.$emit('drag-start', headerKey, index, event);
    },

    onDragOver(headerKey, index, event) {
      if (!this.draggableHeaders) return;
      this.$emit('drag-over', headerKey, index, event);
    },

    onDragLeave(event) {
      if (!this.draggableHeaders) return;
      this.$emit('drag-leave', event);
    },

    onDrop(headerKey, index, event) {
      if (!this.draggableHeaders) return;
      this.$emit('drop', headerKey, index, event);
    },

    onDragEnd() {
      if (!this.draggableHeaders) return;
      this.$emit('drag-end');
    }
  }
};
</script>

<style lang="scss" scoped>
@import './TMResultsDataTable.scss';
</style>
